import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  MapContainer,
  TileLayer,
  LayersControl,
  GeoJSON,
  ScaleControl,
} from "react-leaflet";
import { Box, CircularProgress, Typography } from "@mui/material";
import "../../static/styles/map.css";
import MapFlyTo from "../Maps/components/MapFlyTo";
import FullScreenButton from "../Maps/components/FullScreenButton";
import MouseIndicator from "../Maps/components/MouseIndicator";
import LockMapButton from "../Maps/components/LockMapButton";
import LeafletRuler from "../Maps/components/LeafletRuler";
import DropdownMenu from "../Maps/components/DropdownMenu";
import AOIDrawControl from "../Maps/components/AOIDrawControl";
import MarkerCluster from "../Maps/components/MarkerCluster";
import wellknown from "wellknown";
import {
  canvasRenderer,
} from "../Maps/components/HightlightFeature";
import FileUpload from "../Maps/components/FileUpload";
import PlaceSearch from "../components/PlaceSearch";
import { latLngBounds } from "leaflet";

const HomeMap = ({
  initialCenter,
  pipes,
  markers,
  setCustomAOI,
  AOIGeoJSON,
}) => {
  const [isGeoJSONActive, setGeoJSONActive] = useState(false);
  const [isPipesActive, setPipesActive] = useState(true);
  const [isMapLocked, setIsMapLocked] = useState(false);
  const [zoom, setZoom] = useState(7);
  const navigate = useNavigate();
  const mapRef = useRef(null);
  const [uploadedGeoJSON, setUploadedGeoJSON] = useState(null);
  const [center, setCenter] = useState(initialCenter);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedPipe, setSelectedPipe] = useState(null);

  useEffect(() => {
    if (markers?.length === 1) {
      const firstMarker = markers[0];
      const newCenter = [
        firstMarker.geometry.coordinates[1],
        firstMarker.geometry.coordinates[0],
      ];
      setCenter(newCenter);
      setZoom(10);
    }
  }, [markers]);

  useEffect(() => {
    if (pipes?.length === 1 && mapRef.current) {
      const firstPipe = pipes[0];
      if (firstPipe.geometry.type === "MultiPolygon") {
        const bounds = latLngBounds();
        firstPipe.geometry.coordinates.forEach((polygon) => {
          polygon.forEach((ring) => {
            ring.forEach(([lng, lat]) => {
              bounds.extend([lat, lng]);
            });
          });
        });
        const newZoom = Math.min(
          mapRef.current.getBoundsZoom(bounds, false),
          15
        );
        setZoom(newZoom);
        setCenter(bounds.getCenter());
      }
    }
  }, [pipes]);

  useEffect(() => {
    setCenter(initialCenter);
    setIsLoading(false);
  }, [initialCenter]);

  const handleLockToggle = (isLocked) => {
    setIsMapLocked(isLocked);
  };

  const buttons = [
    {
      type: "AOI",
      activeState: isGeoJSONActive,
      setActiveState: setGeoJSONActive,
    },
    {
      type: "Pipes",
      activeState: isPipesActive,
      setActiveState: setPipesActive,
    },
  ];

  useEffect(() => {
    if (mapRef.current) {
      mapRef.current.on("zoomend", () => {
        setZoom(mapRef.current.getZoom());
      });
    }
  }, [mapRef.current]);

  const handleDrawComplete = (geojson) => {
    setCustomAOI(wellknown.stringify(geojson.geometry));
  };

  const handleDrawDeleted = () => {
    setCustomAOI(false);
  };

  const handlePipeClick = (event, pipe) => {
    setSelectedPipe(pipe);
    const bounds = event.target.getBounds();
    const center = bounds.getCenter();
    setCenter([center.lat, center.lng]);
    setZoom(7);
  };

  const handleLeakPopupClick = (marker) => {
    let prevFilters = JSON.parse(sessionStorage.getItem("selectedFilters"));
    let newFilters = { ...prevFilters, leak: marker };

    sessionStorage.setItem("selectedFilters", JSON.stringify(newFilters)); //TODO: esto va a fallar si nos guardamos el leak_id
    navigate(`/observations/${marker.id}`);
  };

  if (isLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100%",
          width: "100%",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      className="map-app-leak heatmap"
      sx={{ height: "100%", width: "100%", position: "relative" }}
    >
      <MapContainer
        center={center}
        zoom={zoom}
        style={{ height: "100%", width: "99%", borderRadius: "15px" }}
        ref={mapRef}
      >
        <PlaceSearch setCenter={setCenter} setZoom={setZoom} />
        <MouseIndicator />
        <MapFlyTo center={center} zoom={zoom} isFlyToEnabled={!isMapLocked} />
        <ScaleControl position="bottomleft" />
        <LeafletRuler />

        <Box
          className="map-button-container"
          sx={{
            position: "absolute",
            zIndex: 1000,
            display: "flex",
            flexDirection: "column",
            gap: 1,
          }}
        >
          <FullScreenButton />
          <LockMapButton map={mapRef.current} onLockToggle={handleLockToggle} />
          <FileUpload
            onFileLoad={setUploadedGeoJSON}
            setCenter={setCenter}
            setZoom={setZoom}
          />
          <DropdownMenu buttons={buttons} />
        </Box>

        <LayersControl position="bottomright">
          <LayersControl.BaseLayer name="Relief" checked>
            <TileLayer
              url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
              minZoom={4}
              maxZoom={30}
              maxNativeZoom={17}
            />
          </LayersControl.BaseLayer>
          <LayersControl.BaseLayer name="Street Map">
            <TileLayer
              url="http://{s}.google.com/vt/lyrs=m&x={x}&y={y}&z={z}"
              subdomains={["mt0", "mt1", "mt2", "mt3"]}
              minZoom={4}
              maxZoom={30}
            />
          </LayersControl.BaseLayer>
        </LayersControl>

        <AOIDrawControl
          onDrawComplete={handleDrawComplete}
          isDrawing={true}
          onDrawDeleted={handleDrawDeleted}
        />
      asdf

        {markers && markers.length > 0 && (
          <MarkerCluster
            points={markers}
            onMarkerClicked={handleLeakPopupClick}
          />
        )}

        {isPipesActive &&
          pipes &&
          pipes.map((pipe, index) => (
            <GeoJSON
              key={`pipe-${pipe.id}-${index}`}
              data={{
                ...pipe,
                object: "PIPE",
              }}
              className={"pipe-geometry"}
              renderer={canvasRenderer}
              style={(_) => ({
                color:
                  selectedPipe && selectedPipe.id === pipe.id
                    ? "#ff0000"
                    : "#3388ff",
                weight: selectedPipe && selectedPipe.id === pipe.id ? 4 : 2,
              })}
              eventHandlers={{
                click: (event) => handlePipeClick(event, pipe),
              }}
            />
          ))}
        {isGeoJSONActive &&
          AOIGeoJSON &&
          AOIGeoJSON.map((aoi, index) => {
            if (
              // tengo que quitar esto por una restriccion de la db
              aoi.properties.name === "Libya" ||
              aoi.properties.name === "ALG" ||
              aoi.properties.name === "OMAN"
            ) {
              return null;
            }
            return (
              <GeoJSON
                key={index}
                data={{
                  ...aoi,
                  object: "AOI",
                }}
                className={"aoi-geometry"}
              />
            );
          })}
        {uploadedGeoJSON && (
          <GeoJSON data={uploadedGeoJSON} renderer={canvasRenderer} />
        )}
      </MapContainer>

      {(!markers || markers.length === 0) && (!pipes || pipes.length === 0) && (
        <Box
          sx={{
            width: "60%",
            height: "15%",
            position: "absolute",
            top: 0,
            left: "20%",
            right: "50%",
            bottom: 0,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 1005,
            backgroundColor: "rgba(255, 255, 255, 0.7)",
          }}
        >
          <Typography variant="h6">No map data available</Typography>
        </Box>
      )}
    </Box>
  );
};

export default HomeMap;
